import React, {useCallback, useEffect, useState} from 'react';
import {View, ScrollView, StyleSheet, RefreshControl} from 'react-native';
import {
  Text,
  Card,
  ActivityIndicator,
  Snackbar,
  Dialog,
  Portal,
  Button,
  TextInput,
  IconButton,
} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {workerTypeApi} from '../../services/api/workerTypeApi';
import {colors, spacing} from '../../theme';

const WorkerTypeListScreen = () => {
  const {t} = useTranslation();

  const [workerTypes, setWorkerTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [snackbar, setSnackbar] = useState('');

  // Edit dialog state
  const [dialogVisible, setDialogVisible] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [displayName, setDisplayName] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const response = await workerTypeApi.listWorkerTypes();
      setWorkerTypes(response.data.data);
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    }
  }, [t]);

  useEffect(() => {
    setIsLoading(true);
    fetchData().finally(() => setIsLoading(false));
  }, [fetchData]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  }, [fetchData]);

  const handleToggle = useCallback(async (item, newValue) => {
    try {
      await workerTypeApi.updateSetting(item.id, {isEnabled: newValue});
      await fetchData();
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    }
  }, [fetchData, t]);

  const handleDisable = useCallback(async (item) => {
    try {
      await workerTypeApi.updateSetting(item.id, {isEnabled: false});
      await fetchData();
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    }
  }, [fetchData, t]);

  const openEditDialog = useCallback((item) => {
    setEditingItem(item);
    setDisplayName(item.displayNameOverride || item.name);
    setDialogVisible(true);
  }, []);

  const handleSaveOverride = useCallback(async () => {
    if (!editingItem) return;
    try {
      await workerTypeApi.updateSetting(editingItem.id, {
        displayNameOverride: displayName.trim() || null,
      });
      setDialogVisible(false);
      setEditingItem(null);
      await fetchData();
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    }
  }, [editingItem, displayName, fetchData, t]);

  if (isLoading && workerTypes.length === 0) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <ScrollView
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} colors={[colors.primary]} />
        }>
        {workerTypes.length === 0 ? (
          <Text style={styles.emptyText}>{t('workerType.noData')}</Text>
        ) : (
          workerTypes.slice().sort((a, b) => (b.isEnabled ? 1 : 0) - (a.isEnabled ? 1 : 0)).map(wt => (
            <Card key={wt.id} style={[styles.card, !wt.isEnabled && styles.cardDisabled]}>
              <Card.Content style={styles.cardContent}>
                <View style={styles.cardHeader}>
                  <IconButton
                    icon={wt.isEnabled ? 'check-circle' : 'circle-outline'}
                    size={24}
                    iconColor={wt.isEnabled ? colors.primary : colors.textSecondary}
                    onPress={() => handleToggle(wt, !wt.isEnabled)}
                    style={styles.toggleBtn}
                  />
                  <View style={styles.cardText}>
                    <Text variant="titleMedium" style={[styles.featureName, !wt.isEnabled && styles.textDisabled]}>
                      {wt.displayNameOverride || wt.name}
                    </Text>
                    {wt.description ? (
                      <Text variant="bodySmall" style={styles.description}>
                        {wt.description}
                      </Text>
                    ) : null}
                  </View>
                  <IconButton
                    icon="pencil-outline"
                    size={20}
                    iconColor={colors.textSecondary}
                    onPress={() => openEditDialog(wt)}
                    style={styles.actionBtn}
                  />
                  {wt.isEnabled && (
                    <IconButton
                      icon="delete-outline"
                      size={20}
                      iconColor={colors.error}
                      onPress={() => handleDisable(wt)}
                      style={styles.actionBtn}
                    />
                  )}
                </View>
              </Card.Content>
            </Card>
          ))
        )}
        <View style={{height: 40}} />
      </ScrollView>

      {/* Edit Override Dialog */}
      <Portal>
        <Dialog
          visible={dialogVisible}
          onDismiss={() => setDialogVisible(false)}
          style={{borderRadius: 15}}>
          <Dialog.Title>{t('workerType.editOverride')}</Dialog.Title>
          <Dialog.Content>
            <TextInput
              label={t('workerType.displayName')}
              value={displayName}
              onChangeText={setDisplayName}
              mode="outlined"
              style={styles.dialogInput}
            />
            <TextInput
              label={t('workerType.code')}
              value={editingItem?.code || ''}
              mode="outlined"
              style={styles.dialogInput}
              disabled
            />
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setDialogVisible(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              mode="contained"
              onPress={handleSaveOverride}
              style={styles.saveButton}>
              {t('common.save')}
            </Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>

      <Snackbar visible={!!snackbar} onDismiss={() => setSnackbar('')} duration={3000}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  loadingContainer: {flex: 1, justifyContent: 'center', alignItems: 'center'},
  emptyText: {
    textAlign: 'center',
    color: colors.textSecondary,
    marginTop: spacing.xxl,
    paddingHorizontal: spacing.md,
  },
  card: {
    marginHorizontal: spacing.md,
    marginTop: spacing.sm,
    backgroundColor: colors.surface,
    borderRadius: 8,
  },
  cardDisabled: {
    opacity: 0.5,
  },
  cardContent: {paddingVertical: spacing.sm},
  cardHeader: {flexDirection: 'row', alignItems: 'center'},
  toggleBtn: {margin: 0},
  cardText: {flex: 1},
  featureName: {fontWeight: '600', color: colors.text},
  textDisabled: {color: colors.textSecondary},
  description: {color: colors.textSecondary, marginTop: 2},
  actionBtn: {margin: 0},
  dialogInput: {
    marginBottom: spacing.sm,
    backgroundColor: colors.surface,
  },
  saveButton: {
    backgroundColor: colors.primary,
    borderRadius: 10,
    paddingHorizontal: 10,
  },
});

export default WorkerTypeListScreen;
